import React, { useEffect, useCallback, useMemo } from 'react';
import { useQuery } from 'js/hooks/useQuery';
import { connect, useStore } from 'react-redux';
import ProductManagementDashboard from '../components/ProducrManagementDashboard';
import { listRecent as listRecentUserGroupAssessments } from 'js/actions/user-group-assessment-actions';
import { list as listFavoriteUserGroups } from 'js/actions/favorite-user-group-actions';
import { useParams } from 'react-router-dom';
import { SCHEMAS } from 'js/api/schemas';
import { denormalize } from 'normalizr';
import { listUserGroups } from 'js/actions/user-group-actions';
import { listCapabilitySets } from 'js/actions/capability-actions';
import { useStandardCollator } from 'js/utils/string';

function ProductManagementDashboardContainer({
  listRecentUserGroupAssessments,
  listUserGroups,
  listCapabilitySets,
  listFavoriteUserGroups,
}) {
  const { workspaceId } = useParams();
  const store = useStore();
  const collator = useStandardCollator();

  const refreshFavoriteUserGroupsQuery = useCallback(
    (queryId) => listFavoriteUserGroups(workspaceId, queryId),
    [workspaceId, listFavoriteUserGroups]
  );

  const {
    refresh: refreshFavoriteUserGroups,
    result: favoriteUserGroups,
    hasLoaded: favoriteUserGroupsHaveLoaded,
  } = useQuery({
    queryId: `favorite-user-groups-${workspaceId}`,
    query: refreshFavoriteUserGroupsQuery,
    schema: SCHEMAS.FAVORITE_USER_GROUP_ARRAY,
  });

  const refreshUserGroupsQuery = useCallback(
    (queryId) => listUserGroups(workspaceId, queryId),
    [workspaceId, listUserGroups]
  );

  const { refresh: refreshUserGroups, result: userGroups } = useQuery({
    queryId: `user-groups-${workspaceId}`,
    query: refreshUserGroupsQuery,
    schema: SCHEMAS.USER_GROUP_ARRAY,
  });

  const userGroupMap = useMemo(() => {
    return (
      userGroups?.reduce((acc, ug) => {
        acc[ug.id] = ug;
        return acc;
      }, {}) ?? {}
    );
  }, [userGroups]);

  const refreshRecentQuery = useCallback(
    (queryId) => listRecentUserGroupAssessments(queryId),
    [listRecentUserGroupAssessments]
  );

  const {
    refresh: refreshRecentUserGroupAssessments,
    result: recentUserGroupAssessmentsRaw,
    hasLoaded: recentUserGroupAssessmentsHaveLoaded,
  } = useQuery({
    queryId: `recent-user-group-assessments-${workspaceId}`,
    query: refreshRecentQuery,
    schema: SCHEMAS.USER_GROUP_ASSESSMENT_ARRAY,
  });

  const entities = store.getState().entities;
  const recentUserGroupAssessments = useMemo(() => {
    return recentUserGroupAssessmentsRaw == null
      ? null
      : denormalize(
          recentUserGroupAssessmentsRaw || [],
          SCHEMAS.USER_GROUP_ASSESSMENT_ARRAY,
          entities
        ).map((a) => {
          return {
            ...a,
            user_group: userGroupMap[a.user_group_id],
          };
        });
  }, [recentUserGroupAssessmentsRaw, entities, userGroupMap]);

  const refreshCapabilitySetsQuery = useCallback(
    (queryId) => listCapabilitySets(workspaceId, queryId),
    [workspaceId, listCapabilitySets]
  );

  const {
    refresh: refreshCapabilitySets,
    result: capabilitySetsRaw,
    hasLoaded: capabilitySetsHaveLoaded,
  } = useQuery({
    queryId: `capability-sets-${workspaceId}`,
    query: refreshCapabilitySetsQuery,
  });

  useEffect(() => {
    refreshCapabilitySets();
  }, [refreshCapabilitySets]);

  const capabilitySets = useMemo(
    () =>
      denormalize(
        capabilitySetsRaw || [],
        SCHEMAS.CAPABILITY_SET_ARRAY,
        entities
      ).sort(
        (a, b) =>
          collator.compare(a.name, b.name) || collator.compare(a.id, b.id)
      ),
    [capabilitySetsRaw, collator, entities]
  );

  useEffect(() => {
    refreshRecentUserGroupAssessments();
  }, [refreshRecentUserGroupAssessments]);

  useEffect(() => {
    refreshUserGroups();
  }, [refreshUserGroups]);

  useEffect(() => {
    refreshFavoriteUserGroups();
  }, [refreshFavoriteUserGroups]);

  const myUserGroups = useMemo(() => {
    const items = (favoriteUserGroups ?? []).filter(
      (item) => item?.user_group && item.user_group.archived_state == null
    );

    return [...items].sort((a, b) => {
      const fav = Number(!!b.is_favorited) - Number(!!a.is_favorited);
      if (fav !== 0) return fav;
      const nameCmp = collator.compare(
        a.user_group?.name ?? '',
        b.user_group?.name ?? ''
      );
      if (nameCmp !== 0) return nameCmp;
      const idA = a.user_group?.id ?? '';
      const idB = b.user_group?.id ?? '';
      return collator.compare(String(idA), String(idB));
    });
  }, [favoriteUserGroups, collator]);

  return (
    <ProductManagementDashboard
      recentUserGroupAssessments={recentUserGroupAssessments}
      recentUserGroupAssessmentsHaveLoaded={
        recentUserGroupAssessmentsHaveLoaded
      }
      capabilitySets={capabilitySets}
      capabilitySetsHaveLoaded={capabilitySetsHaveLoaded}
      myUserGroups={myUserGroups}
      myUserGroupsHaveLoaded={favoriteUserGroupsHaveLoaded}
    />
  );
}

export default connect(null, {
  listRecentUserGroupAssessments,
  listUserGroups,
  listCapabilitySets,
  listFavoriteUserGroups,
})(ProductManagementDashboardContainer);
